/**
 * @fileoverview Search V2 Component
 * Sprint 10: Buyer App 2.0 (AI + Personalization + Conversion Engine)
 * 
 * AI-powered search:
 * - Typo-tolerant
 * - Semantic understanding (Filipino ingredients)
 * - Real-time autocomplete
 * - Smart filters
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Search,
  X,
  Filter,
  Clock,
  Star,
  TrendingUp,
  Store,
  Package,
  Grid,
  ChevronRight,
  Loader2,
  Sparkles,
} from 'lucide-react';

// Types
interface SearchResult {
  products: ProductResult[];
  vendors: VendorResult[];
  categories: CategoryMatch[];
  suggestions: string[];
  totalProducts: number;
  totalVendors: number;
  searchTime: number;
  query: {
    original: string;
    normalized: string;
    semanticExpansion: string[];
  };
}

interface ProductResult {
  productId: string;
  productName: string;
  vendorId: string;
  vendorName: string;
  price: number;
  unit: string;
  imageUrl?: string;
  category: string;
  stockStatus: string;
  rating?: number;
  prepTime?: number;
  reliabilityScore?: number;
  relevanceScore: number;
  matchReason: string;
}

interface VendorResult {
  vendorId: string;
  vendorName: string;
  imageUrl?: string;
  category: string;
  rating: number;
  prepTime: number;
  reliabilityScore: number;
  productCount: number;
  matchReason: string;
}

interface CategoryMatch {
  category: string;
  displayName: string;
  productCount: number;
}

interface AutocompleteSuggestion {
  type: 'product' | 'vendor' | 'category';
  text: string;
  count?: number;
}

interface SearchV2Props {
  onProductClick?: (productId: string, vendorId: string) => void;
  onVendorClick?: (vendorId: string) => void;
}

export default function SearchV2({ 
  onProductClick = () => {}, 
  onVendorClick = () => {} 
}: SearchV2Props) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult | null>(null);
  const [suggestions, setSuggestions] = useState<AutocompleteSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<NodeJS.Timeout>();

  // Filters
  const [sortBy, setSortBy] = useState<string>('relevance');
  const [category, setCategory] = useState<string>('');
  const [inStock, setInStock] = useState(true);

  // Autocomplete
  useEffect(() => {
    if (query.length < 2) {
      setSuggestions([]);
      setShowAutocomplete(false);
      return;
    }

    // Debounce autocomplete
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(async () => {
      try {
        const response = await fetch(`/api/search/v2/autocomplete?q=${encodeURIComponent(query)}`);
        if (response.ok) {
          const data = await response.json();
          setSuggestions(data.suggestions);
          setShowAutocomplete(true);
        }
      } catch (err) {
        console.error('Autocomplete failed', err);
      }
    }, 200);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query]);

  // Search
  const handleSearch = useCallback(async (searchQuery: string = query) => {
    if (!searchQuery.trim()) return;

    setLoading(true);
    setShowAutocomplete(false);

    try {
      const params = new URLSearchParams({
        q: searchQuery,
        sortBy,
        inStock: String(inStock),
      });
      if (category) params.set('category', category);

      const response = await fetch(`/api/search/v2?${params}`);
      if (response.ok) {
        const data = await response.json();
        setResults(data);
      }
    } catch (err) {
      console.error('Search failed', err);
    } finally {
      setLoading(false);
    }
  }, [query, sortBy, category, inStock]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleSuggestionClick = (text: string) => {
    setQuery(text);
    setShowAutocomplete(false);
    handleSearch(text);
  };

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Search Header */}
      <div className="bg-white sticky top-0 z-20 shadow-sm">
        <div className="p-4">
          <div className="relative">
            <div className="flex items-center gap-2 px-4 py-3 bg-gray-100 rounded-xl">
              <Search className="w-5 h-5 text-gray-400" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                onFocus={() => query.length >= 2 && setShowAutocomplete(true)}
                placeholder="Search products, vendors, dishes..."
                className="flex-1 bg-transparent outline-none text-gray-800"
              />
              {query && (
                <button
                  onClick={() => { setQuery(''); setResults(null); }}
                  className="p-1 hover:bg-gray-200 rounded-full"
                >
                  <X className="w-4 h-4 text-gray-400" />
                </button>
              )}
              {loading && <Loader2 className="w-5 h-5 text-emerald-600 animate-spin" />}
            </div>

            {/* Autocomplete Dropdown */}
            {showAutocomplete && suggestions.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden z-30">
                {suggestions.map((suggestion, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleSuggestionClick(suggestion.text)}
                    className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 transition text-left"
                  >
                    {suggestion.type === 'product' && <Package className="w-4 h-4 text-emerald-600" />}
                    {suggestion.type === 'vendor' && <Store className="w-4 h-4 text-blue-600" />}
                    {suggestion.type === 'category' && <Grid className="w-4 h-4 text-purple-600" />}
                    <span className="flex-1">{suggestion.text}</span>
                    {suggestion.count && (
                      <span className="text-xs text-gray-400">{suggestion.count}</span>
                    )}
                    <ChevronRight className="w-4 h-4 text-gray-300" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Quick Filters */}
          <div className="flex items-center gap-2 mt-3 overflow-x-auto pb-1">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium transition ${
                showFilters ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-700'
              }`}
            >
              <Filter className="w-4 h-4" />
              Filters
            </button>
            <button
              onClick={() => { setSortBy('price_low'); handleSearch(); }}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition ${
                sortBy === 'price_low' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-700'
              }`}
            >
              Lowest Price
            </button>
            <button
              onClick={() => { setSortBy('rating'); handleSearch(); }}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition ${
                sortBy === 'rating' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-700'
              }`}
            >
              Top Rated
            </button>
            <button
              onClick={() => { setSortBy('prep_time'); handleSearch(); }}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition ${
                sortBy === 'prep_time' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-700'
              }`}
            >
              Fastest
            </button>
          </div>
        </div>

        {/* Extended Filters */}
        {showFilters && (
          <div className="px-4 pb-4 border-t border-gray-100 pt-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Sort By</label>
                <select
                  value={sortBy}
                  onChange={(e) => { setSortBy(e.target.value); handleSearch(); }}
                  className="w-full px-3 py-2 bg-gray-100 rounded-lg text-sm"
                >
                  <option value="relevance">Relevance</option>
                  <option value="price_low">Price: Low to High</option>
                  <option value="price_high">Price: High to Low</option>
                  <option value="rating">Highest Rated</option>
                  <option value="prep_time">Fastest Prep</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Stock</label>
                <select
                  value={String(inStock)}
                  onChange={(e) => { setInStock(e.target.value === 'true'); handleSearch(); }}
                  className="w-full px-3 py-2 bg-gray-100 rounded-lg text-sm"
                >
                  <option value="true">In Stock Only</option>
                  <option value="false">Show All</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Results */}
      {results && (
        <div className="p-4">
          {/* Query Info */}
          <div className="mb-4 p-3 bg-white rounded-xl">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">
                  Found <span className="font-bold text-gray-800">{results.totalProducts}</span> products
                </p>
                {results.query.semanticExpansion.length > 0 && (
                  <p className="text-xs text-emerald-600 flex items-center gap-1 mt-1">
                    <Sparkles className="w-3 h-3" />
                    Also searching: {results.query.semanticExpansion.slice(0, 3).join(', ')}
                  </p>
                )}
              </div>
              <span className="text-xs text-gray-400">{results.searchTime}ms</span>
            </div>
          </div>

          {/* Category Filters */}
          {results.categories.length > 0 && (
            <div className="mb-4 flex gap-2 overflow-x-auto pb-1">
              <button
                onClick={() => { setCategory(''); handleSearch(); }}
                className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition ${
                  !category ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-700'
                }`}
              >
                All
              </button>
              {results.categories.map((cat) => (
                <button
                  key={cat.category}
                  onClick={() => { setCategory(cat.category); handleSearch(); }}
                  className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition ${
                    category === cat.category ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  {cat.displayName} ({cat.productCount})
                </button>
              ))}
            </div>
          )}

          {/* Vendors */}
          {results.vendors.length > 0 && (
            <div className="mb-6">
              <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                <Store className="w-5 h-5 text-blue-600" />
                Vendors
              </h3>
              <div className="flex gap-3 overflow-x-auto pb-2">
                {results.vendors.map((vendor) => (
                  <button
                    key={vendor.vendorId}
                    onClick={() => onVendorClick(vendor.vendorId)}
                    className="flex-shrink-0 w-36 bg-white rounded-xl p-3 shadow-sm hover:shadow-md transition text-left"
                  >
                    <p className="font-semibold text-gray-800 text-sm truncate">{vendor.vendorName}</p>
                    <div className="flex items-center gap-2 mt-1 text-xs">
                      <span className="flex items-center text-amber-600">
                        <Star className="w-3 h-3 mr-0.5 fill-current" />
                        {vendor.rating.toFixed(1)}
                      </span>
                      <span className="flex items-center text-gray-500">
                        <Clock className="w-3 h-3 mr-0.5" />
                        {vendor.prepTime}m
                      </span>
                    </div>
                    <p className="text-[10px] text-blue-600 mt-1">{vendor.matchReason}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Products */}
          <div>
            <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
              <Package className="w-5 h-5 text-emerald-600" />
              Products
            </h3>
            <div className="grid grid-cols-2 gap-3">
              {results.products.map((product) => (
                <button
                  key={product.productId}
                  onClick={() => onProductClick(product.productId, product.vendorId)}
                  className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition text-left"
                >
                  <div className="h-24 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                    {product.imageUrl ? (
                      <img src={product.imageUrl} alt={product.productName} className="w-full h-full object-cover" />
                    ) : (
                      <Package className="w-8 h-8 text-gray-400" />
                    )}
                  </div>
                  <div className="p-3">
                    <p className="font-semibold text-gray-800 text-sm truncate">{product.productName}</p>
                    <p className="text-emerald-600 font-bold">₱{product.price.toFixed(2)}</p>
                    <p className="text-xs text-gray-500 truncate">{product.vendorName}</p>
                    {product.matchReason !== 'Matches search' && (
                      <p className="text-[10px] text-purple-600 mt-1">{product.matchReason}</p>
                    )}
                    <div className="flex items-center gap-2 mt-1">
                      {product.rating && (
                        <span className="flex items-center text-xs text-amber-600">
                          <Star className="w-3 h-3 mr-0.5 fill-current" />
                          {product.rating.toFixed(1)}
                        </span>
                      )}
                      {product.stockStatus !== 'in_stock' && (
                        <span className="text-xs text-red-500">Low stock</span>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* No Results */}
          {results.products.length === 0 && results.vendors.length === 0 && (
            <div className="text-center py-12">
              <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600">No results found for "{results.query.original}"</p>
              {results.suggestions.length > 0 && (
                <div className="mt-4">
                  <p className="text-sm text-gray-500 mb-2">Try searching for:</p>
                  <div className="flex flex-wrap gap-2 justify-center">
                    {results.suggestions.map((sug, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleSuggestionClick(sug)}
                        className="px-3 py-1 bg-emerald-50 text-emerald-700 rounded-full text-sm hover:bg-emerald-100"
                      >
                        {sug}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Empty State */}
      {!results && !loading && (
        <div className="p-4">
          <div className="text-center py-12">
            <Search className="w-16 h-16 text-emerald-200 mx-auto mb-4" />
            <p className="text-gray-600 mb-2">What are you looking for today?</p>
            <p className="text-sm text-gray-400">
              Try "chicken adobo", "fresh vegetables", or a vendor name
            </p>
          </div>

          {/* Trending Searches */}
          <div className="mt-6">
            <p className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-red-500" />
              Trending searches
            </p>
            <div className="flex flex-wrap gap-2">
              {['Chicken', 'Vegetables', 'Sinigang', 'Rice', 'Pork', 'Fish'].map((term) => (
                <button
                  key={term}
                  onClick={() => handleSuggestionClick(term)}
                  className="px-4 py-2 bg-white rounded-xl text-sm text-gray-700 shadow-sm hover:shadow-md transition"
                >
                  {term}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}



